/*
 *  Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Input, Label, Modal } from "@nordcloud/gnui";
import { FormGroup } from "~/components";
import { FormField, FormData, schema } from "./formConfig";

type ModalActions = React.ComponentProps<typeof Modal>["actions"];

type Props = {
  isOpen: boolean;
  onSubmit: (data: FormData) => Promise<void>;
  onClose: () => void;
};

export function AddSearchPresetModal({ isOpen, onClose, onSubmit }: Props) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      [FormField.NAME]: "",
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = () =>
    handleSubmit(async (data) => {
      await onSubmit(data);
      handleClose();
    })();

  const MODAL_ACTIONS: ModalActions = [
    {
      order: 0,
      label: "Cancel",
      onAction: handleClose,
      severity: "low",
    },
    {
      order: 1,
      label: "Save filter",
      onAction: handleSave,
      severity: "high",
    },
  ];

  return (
    <Modal
      actions={MODAL_ACTIONS}
      isOpen={isOpen}
      contentLabel="Save Filter"
      onClose={handleClose}
    >
      <form onSubmit={handleSave}>
        <FormGroup error={errors[FormField.NAME]}>
          <Label required htmlFor={FormField.NAME} name="Filter Preset Name" />
          <Input
            id={FormField.NAME}
            onKeyDown={async (event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                await handleSave();
              }
            }}
            {...register(FormField.NAME)}
            placeholder="e.g. AWS unallocated"
          />
        </FormGroup>
      </form>
    </Modal>
  );
}
